import React, { useState } from 'react';
import { PhotoLog, RESTORATION_PHASES } from '../../types/teardown';
import { formatShortDate } from '../../utils/printFormatter';
import {
  Camera,
  Image as ImageIcon,
  CheckCircle2,
  Calendar,
  Layers,
  Tag,
  Filter,
} from 'lucide-react';

export interface DossierPhotoGridProps {
  photos: PhotoLog[];
  filterCuratedOnly?: boolean;
  className?: string;
  isPrintMode?: boolean;
}

export const DossierPhotoGrid: React.FC<DossierPhotoGridProps> = ({
  photos,
  filterCuratedOnly = true,
  className = '',
  isPrintMode = false,
}) => {
  const [showOnlyCurated, setShowOnlyCurated] = useState<boolean>(filterCuratedOnly);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});

  const handleImageError = (id: string) => {
    setBrokenImages((prev) => ({ ...prev, [id]: true }));
  };

  const filteredPhotos = photos.filter((photo) => {
    if (showOnlyCurated && !photo.includeInDossier && !photo.milestoneProof) {
      return false;
    }
    if (selectedCategory !== 'all' && photo.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  const beforeAfterPairs = filteredPhotos.filter(
    (p) => p.category === 'before_after_pair' || Boolean(p.afterImageUrl)
  );

  const singleEvidencePlates = filteredPhotos.filter(
    (p) => p.category !== 'before_after_pair' && !p.afterImageUrl
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Screen Mode Filter Controls (Hidden in Print) */}
      {!isPrintMode && (
        <div className="no-print bg-shop-dark border border-shop-border p-3.5 rounded shadow-machined flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-safety-amber" />
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-shop-highlight">
              Provenance Photographic Evidence ({filteredPhotos.length} Plates)
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Filter Toggle Button (min-h-[44px], min-w-[44px]) */}
            <button
              type="button"
              onClick={() => setShowOnlyCurated(!showOnlyCurated)}
              className={`min-h-[44px] min-w-[44px] px-3 py-2 rounded text-xs font-mono font-bold uppercase tracking-wider border transition-all duration-100 flex items-center gap-2 ${
                showOnlyCurated
                  ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-[0_2px_0_0_#92400e]'
                  : 'bg-shop-panel text-shop-light border-shop-border hover:bg-shop-well'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{showOnlyCurated ? 'Curated Dossier Only' : 'All Project Photos'}</span>
            </button>

            {/* Category Dropdown (min-h-[44px], min-w-[44px]) */}
            <div className="relative flex items-center">
              <span className="absolute left-2.5 text-shop-muted pointer-events-none">
                <Filter className="w-3.5 h-3.5" />
              </span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="min-h-[44px] pl-8 pr-4 py-2 bg-shop-darkest text-shop-light border border-shop-border rounded text-xs font-mono focus:border-safety-amber focus:outline-none cursor-pointer"
                aria-label="Filter photos by category"
              >
                <option value="all">All Photo Categories</option>
                <option value="before_after_pair">Before &amp; After Pairs</option>
                <option value="teardown_as_found">Teardown / As-Found</option>
                <option value="restoration_in_progress">Restoration In-Progress</option>
                <option value="completed_restored">Completed / Restored</option>
                <option value="disassembly_routing">Disassembly Routing</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredPhotos.length === 0 && (
        <div className="bg-shop-dark border border-dashed border-shop-border rounded p-8 text-center space-y-3 avoid-break">
          <div className="w-12 h-12 bg-shop-panel border border-shop-border rounded-full flex items-center justify-center mx-auto text-shop-muted">
            <ImageIcon className="w-6 h-6" />
          </div>
          <h4 className="text-sm font-bold font-mono uppercase text-shop-highlight">
            No Photographic Evidence Plates Match Selection
          </h4>
          <p className="text-xs text-shop-muted max-w-md mx-auto">
            {showOnlyCurated
              ? 'No photos are currently marked with "Include in Dossier". Switch to "All Project Photos" above or flag photo logs in the Teardown Tracker.'
              : 'No project photos have been logged yet.'}
          </p>
          {showOnlyCurated && (
            <button
              type="button"
              onClick={() => setShowOnlyCurated(false)}
              className="min-h-[44px] px-4 py-2 bg-shop-panel text-shop-highlight border border-shop-border rounded text-xs font-mono font-bold uppercase tracking-wider hover:bg-shop-well transition-colors"
            >
              Show All Available Project Photos
            </button>
          )}
        </div>
      )}

      {/* 1. Before & After Restoration Pairs */}
      {beforeAfterPairs.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 border-b border-shop-border pb-2">
            <Layers className="w-4 h-4 text-safety-green" />
            <h4 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
              Comparative Before &amp; After Restoration Plates ({beforeAfterPairs.length} Pairs)
            </h4>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {beforeAfterPairs.map((pair, index) => {
              const phaseMeta = RESTORATION_PHASES[pair.phase];
              const beforeBroken = brokenImages[`${pair.id}_before`];
              const afterBroken = brokenImages[`${pair.id}_after`];

              return (
                <div
                  key={pair.id}
                  className="bg-shop-dark border-2 border-shop-border rounded overflow-hidden shadow-machined avoid-break dossier-photo-card"
                >
                  {/* Plate Header Bar */}
                  <div className="px-4 py-2.5 bg-shop-darkest border-b border-shop-border flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="bg-safety-amber text-shop-darkest font-mono text-[10px] font-bold px-2 py-0.5 rounded-sm">
                        PLATE #{index + 1}
                      </span>
                      <span className="font-mono text-xs font-bold text-shop-highlight">
                        {pair.title}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] font-mono text-shop-muted">
                      <span className="bg-shop-panel px-2 py-0.5 rounded border border-shop-border text-blueprint-cyan">
                        {phaseMeta?.title || pair.phase}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-shop-muted" />
                        {formatShortDate(pair.capturedAt)}
                      </span>
                    </div>
                  </div>

                  {/* Paired Images Side-by-Side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-shop-border bg-shop-darkest">
                    {/* Before Image */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-center justify-between text-[10px] font-mono">
                        <span className="text-safety-amber font-bold uppercase tracking-wider bg-safety-amber/10 px-2 py-0.5 rounded border border-safety-amber/30">
                          Original As-Found / Teardown
                        </span>
                        <span className="text-shop-muted">Pre-Restoration Baseline</span>
                      </div>
                      <div className="aspect-video bg-shop-panel rounded border border-shop-border overflow-hidden relative flex items-center justify-center">
                        {!beforeBroken && pair.beforeImageUrl ? (
                          <img
                            src={pair.beforeImageUrl}
                            alt={`${pair.title} - Before`}
                            onError={() => handleImageError(`${pair.id}_before`)}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="text-center p-4 space-y-1">
                            <ImageIcon className="w-8 h-8 text-shop-muted mx-auto" />
                            <span className="text-[10px] font-mono text-shop-muted block">
                              As-Found Archive Photo
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* After Image */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-center justify-between text-[10px] font-mono">
                        <span className="text-safety-green font-bold uppercase tracking-wider bg-safety-green/10 px-2 py-0.5 rounded border border-safety-green/30">
                          Restored / Verified Completion
                        </span>
                        <span className="text-shop-muted">Agreed-Value Provenance</span>
                      </div>
                      <div className="aspect-video bg-shop-panel rounded border border-shop-border overflow-hidden relative flex items-center justify-center">
                        {!afterBroken && pair.afterImageUrl ? (
                          <img
                            src={pair.afterImageUrl}
                            alt={`${pair.title} - Restored`}
                            onError={() => handleImageError(`${pair.id}_after`)}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="text-center p-4 space-y-1">
                            <ImageIcon className="w-8 h-8 text-safety-green mx-auto" />
                            <span className="text-[10px] font-mono text-shop-muted block">
                              Restored Component Plate
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description & Technical Notes Footer */}
                  <div className="px-4 py-2.5 bg-shop-dark border-t border-shop-border flex flex-wrap items-center justify-between gap-2 text-xs">
                    <p className="text-shop-light font-sans text-xs leading-relaxed max-w-2xl">
                      {pair.description}
                    </p>
                    {pair.tags && pair.tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                        {pair.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-[10px] font-mono bg-shop-darkest text-shop-muted px-2 py-0.5 rounded border border-shop-border"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. Single Milestone & Machining Plates Grid */}
      {singleEvidencePlates.length > 0 && (
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2 border-b border-shop-border pb-2">
            <Camera className="w-4 h-4 text-safety-blue" />
            <h4 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
              Milestone Execution &amp; Technical Assembly Records ({singleEvidencePlates.length} Plates)
            </h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 dossier-photo-grid">
            {singleEvidencePlates.map((photo, index) => {
              const phaseMeta = RESTORATION_PHASES[photo.phase];
              const isBroken = brokenImages[photo.id];

              return (
                <div
                  key={photo.id}
                  className="bg-shop-dark border border-shop-border rounded overflow-hidden shadow-machined flex flex-col avoid-break dossier-photo-card"
                >
                  {/* Image Container */}
                  <div className="aspect-video bg-shop-darkest relative flex items-center justify-center border-b border-shop-border">
                    {!isBroken && photo.beforeImageUrl ? (
                      <img
                        src={photo.beforeImageUrl}
                        alt={photo.title}
                        onError={() => handleImageError(photo.id)}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="text-center p-4 space-y-1">
                        <ImageIcon className="w-8 h-8 text-shop-muted mx-auto" />
                        <span className="text-[10px] font-mono text-shop-muted block">
                          Inspection Plate #{index + 1}
                        </span>
                      </div>
                    )}

                    {/* Badge Overlay */}
                    <div className="absolute top-2 left-2 flex items-center gap-1">
                      <span className="bg-shop-darkest/90 text-safety-amber font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border border-shop-border">
                        {phaseMeta?.shortCode || 'DOC'}
                      </span>
                      {photo.milestoneProof && (
                        <span className="bg-safety-green text-shop-darkest font-mono text-[9px] font-bold px-1.5 py-0.5 rounded">
                          MILESTONE PROOF
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Metadata Content */}
                  <div className="p-3 flex-1 flex flex-col justify-between space-y-2">
                    <div className="space-y-1">
                      <h5 className="font-mono font-bold text-xs text-shop-highlight line-clamp-1">
                        {photo.title}
                      </h5>
                      <p className="text-[11px] text-shop-muted line-clamp-2 leading-relaxed">
                        {photo.description}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-shop-border flex items-center justify-between text-[10px] font-mono text-shop-muted">
                      <span>{phaseMeta?.title || photo.phase}</span>
                      <span>{formatShortDate(photo.capturedAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
